import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Building, Apartment } from '@/types';

interface AddSensorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSensorAdded: () => void;
}

export function AddSensorDialog({ open, onOpenChange, onSensorAdded }: AddSensorDialogProps) {
  const [deviceId, setDeviceId] = useState('');
  const [name, setName] = useState('');
  const [buildingId, setBuildingId] = useState('');
  const [apartmentId, setApartmentId] = useState('');
  const [location, setLocation] = useState('');
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [apartments, setApartments] = useState<Apartment[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  React.useEffect(() => {
    if (open) {
      fetchBuildings();
      setDeviceId('');
      setName('');
      setBuildingId('');
      setApartmentId('');
      setLocation('');
    }
  }, [open]);

  React.useEffect(() => {
    if (buildingId) {
      fetchApartments(buildingId);
    } else {
      setApartments([]);
      setApartmentId('');
    }
  }, [buildingId]);

  const fetchBuildings = async () => {
    const { data, error } = await supabase
      .from('buildings')
      .select('*')
      .order('name');
    
    if (data && !error) {
      setBuildings(data);
    }
  };

  const fetchApartments = async (buildingId: string) => {
    const { data, error } = await supabase
      .from('apartments')
      .select('*')
      .eq('building_id', buildingId)
      .order('apartment_number');
    
    if (data && !error) {
      setApartments(data);
    }
  };

  const validateDeviceId = (id: string) => {
    return /^[a-zA-Z0-9]{22}$/.test(id);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateDeviceId(deviceId)) {
      toast({
        title: "Invalid Device ID",
        description: "Device ID must be exactly 22 alphanumeric characters",
        variant: "destructive",
      });
      return;
    }

    if (!name || !buildingId || !location) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      const { error } = await supabase
        .from('temperature_sensors')
        .insert([
          {
            device_id: deviceId,
            name,
            building_id: buildingId,
            apartment_id: apartmentId || null,
            location,
            status: 'offline',
            is_online: false,
          }
        ]);

      if (error) throw error;

      toast({
        title: "Sensor Added",
        description: "Temperature sensor has been successfully added",
      });

      onSensorAdded();
      onOpenChange(false);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to add sensor. Device ID might already exist.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Add Temperature Sensor</DialogTitle>
          <DialogDescription>
            Add a new ETLTS001 temperature and humidity sensor to the system.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="deviceId">Device ID *</Label>
            <Input
              id="deviceId"
              value={deviceId}
              onChange={(e) => setDeviceId(e.target.value.toUpperCase())}
              placeholder="Enter 22-character device ID"
              maxLength={22}
              className={!validateDeviceId(deviceId) && deviceId ? 'border-red-500' : ''}
            />
            <p className="text-xs text-muted-foreground">
              Must be exactly 22 alphanumeric characters
            </p>
            {deviceId && !validateDeviceId(deviceId) && (
              <p className="text-xs text-red-500">
                Invalid format: {deviceId.length}/22 characters
              </p>
            )}
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="name">Sensor Name *</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Living Room Sensor"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="building">Building *</Label>
            <Select value={buildingId} onValueChange={setBuildingId}>
              <SelectTrigger>
                <SelectValue placeholder="Select building" />
              </SelectTrigger>
              <SelectContent>
                {buildings.map((building) => (
                  <SelectItem key={building.id} value={building.id}>
                    {building.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="apartment">Apartment (Optional)</Label>
            <Select value={apartmentId} onValueChange={setApartmentId} disabled={!buildingId}>
              <SelectTrigger>
                <SelectValue placeholder="Select apartment (optional)" />
              </SelectTrigger>
              <SelectContent>
                {apartments.map((apartment) => (
                  <SelectItem key={apartment.id} value={apartment.id}>
                    {apartment.apartment_number}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="location">Location *</Label>
            <Input
              id="location"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="e.g., Living Room, Kitchen, Bedroom"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading || !validateDeviceId(deviceId)}>
              {isLoading ? 'Adding...' : 'Add Sensor'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}